import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SavedViewService } from '../../services/saved-view.service';
import { SavedViewContext } from '../../models/saved-view.model';

export interface SavedViewOption {
  name: string;
  summary: string;
}

@Component({
  selector: 'app-saved-views-menu',
  imports: [TranslatePipe],
  templateUrl: './saved-views-menu.component.html',
  styleUrl: './saved-views-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedViewsMenuComponent {
  private readonly savedViewService = inject(SavedViewService);

  readonly views = input<SavedViewOption[]>([]);
  readonly activeName = input<string | null>(null);
  readonly context = input.required<SavedViewContext>();
  readonly projectId = input.required<string>();

  readonly viewSelected = output<string>();
  readonly viewSaved = output<string>();
  readonly viewDeleted = output<string>();
  readonly viewDeleteFailed = output<string>();

  readonly open = signal(false);
  readonly nameInput = signal('');

  readonly canSave = computed(() => this.nameInput().trim().length > 0);
  readonly viewsCount = computed(() => this.views().length);
  readonly buttonLabelKey = computed(() => (this.activeName() ? null : 'savedViews.button'));

  private readonly root = viewChild<ElementRef<HTMLElement>>('root');

  toggle(): void {
    this.open.update((v) => !v);
  }

  close(): void {
    this.open.set(false);
    this.nameInput.set('');
  }

  onSelect(name: string): void {
    this.viewSelected.emit(name);
    this.close();
  }

  onDelete(event: Event, name: string): void {
    event.stopPropagation();
    this.savedViewService.delete(this.projectId(), this.context(), name).subscribe({
      next: () => this.viewDeleted.emit(name),
      error: () => this.viewDeleteFailed.emit(name),
    });
  }

  onNameInput(event: Event): void {
    this.nameInput.set((event.target as HTMLInputElement).value);
  }

  onSaveKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.canSave()) {
      event.preventDefault();
      this.save();
    }
  }

  save(): void {
    const name = this.nameInput().trim();
    if (!name) return;
    this.viewSaved.emit(name);
    this.close();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    const root = this.root()?.nativeElement;
    if (root && !root.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.open()) {
      this.close();
    }
  }
}
